import ExcelJS from "exceljs";

const BRAND_COLOR_1 = "FF004771"; // Contractable Brand Color 1 (#004771)
const WHITE = "FFFFFFFF";
const ZEBRA = "FFF8FAFC";
const TOTAL_FILL = "FFEAF2F8";
const BORDER_COLOR = "FFD5D7DA";

const HOURS_FORMAT = "#,##0.00";
const COST_FORMAT = "$#,##0.00";

const thinBorder = {
  top: { style: "thin", color: { argb: BORDER_COLOR } },
  left: { style: "thin", color: { argb: BORDER_COLOR } },
  bottom: { style: "thin", color: { argb: BORDER_COLOR } },
  right: { style: "thin", color: { argb: BORDER_COLOR } },
};

/**
 * Column definitions shared by both sheets. `format` is applied to data cells.
 *
 * @param {boolean} withProject - Include the Project Name column (Project Breakdown sheet)
 * @returns {Array<{header: string, key: string, format?: string, align: string, bold?: boolean}>}
 */
const getColumnDefs = (withProject) => [
  { header: "Emp Name", key: "empName", align: "left" },
  ...(withProject
    ? [{ header: "Project Name", key: "projectName", align: "left" }]
    : []),
  {
    header: "Total Entries",
    key: "totalEntries",
    format: "0",
    align: "center",
  },
  { header: "Standard Hours", key: "standardHours", format: HOURS_FORMAT, align: "right" },
  { header: "Standard Cost", key: "standardCost", format: COST_FORMAT, align: "right" },
  { header: "Overtime Hours", key: "overtimeHours", format: HOURS_FORMAT, align: "right" },
  { header: "Overtime Cost", key: "overtimeCost", format: COST_FORMAT, align: "right" },
  { header: "Holiday Hours", key: "holidayHours", format: HOURS_FORMAT, align: "right" },
  { header: "Holiday Cost", key: "holidayCost", format: COST_FORMAT, align: "right" },
  { header: "Total Hours", key: "totalHours", format: HOURS_FORMAT, align: "right", bold: true },
  { header: "Total Cost", key: "totalCost", format: COST_FORMAT, align: "right", bold: true },
];

/**
 * Builds a styled worksheet: navy header, zebra rows, number formats, bordered cells,
 * frozen header row, auto column widths and a highlighted TOTAL row.
 *
 * @param {ExcelJS.Workbook} workbook - Target workbook
 * @param {string} sheetName - Worksheet name
 * @param {Array<Object>} rows - Data rows (keys must match column defs)
 * @param {Object} totalRow - TOTAL row values
 * @param {boolean} withProject - Whether the Project Name column is present
 * @returns {ExcelJS.Worksheet} Created worksheet
 */
const buildStyledSheet = (workbook, sheetName, rows, totalRow, withProject) => {
  const columnDefs = getColumnDefs(withProject);
  const sheet = workbook.addWorksheet(sheetName, {
    views: [{ state: "frozen", ySplit: 1 }],
  });

  sheet.columns = columnDefs.map((col) => ({ header: col.header, key: col.key }));

  const headerRow = sheet.getRow(1);
  headerRow.height = 26;
  headerRow.eachCell((cell, colNumber) => {
    cell.font = { bold: true, color: { argb: WHITE }, size: 11 };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BRAND_COLOR_1 } };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
    };
    cell.border = thinBorder;
  });

  rows.forEach((rowData, index) => {
    const row = sheet.addRow(rowData);
    row.height = 20;
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const def = columnDefs[colNumber - 1];
      cell.border = thinBorder;
      cell.alignment = { vertical: "middle", horizontal: def.align };
      cell.font = { bold: Boolean(def.bold) };
      if (def.format) cell.numFmt = def.format;
      if (index % 2 === 1) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ZEBRA } };
      }
    });
  });

  const footer = sheet.addRow(totalRow);
  footer.height = 24;
  footer.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const def = columnDefs[colNumber - 1];
    cell.font = { bold: true, color: { argb: BRAND_COLOR_1 } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: TOTAL_FILL } };
    cell.alignment = { vertical: "middle", horizontal: def.align };
    cell.border = { ...thinBorder, top: { style: "medium", color: { argb: BRAND_COLOR_1 } } };
    if (def.format) cell.numFmt = def.format;
  });

  columnDefs.forEach((def, index) => {
    const longest = Math.max(
      def.header.length,
      ...rows.map((r) => String(r[def.key] ?? "").length),
      String(totalRow[def.key] ?? "").length
    );
    sheet.getColumn(index + 1).width = Math.min(Math.max(longest + 4, 14), 48);
  });

  return sheet;
};

/**
 * Vertically merges consecutive rows belonging to the same employee (by userId)
 * on the Project Breakdown sheet.
 *
 * @param {ExcelJS.Worksheet} sheet - Project Breakdown worksheet
 * @param {Array<Object>} rows - Data rows used to build the sheet (excluding header/total)
 * @returns {void}
 */
const mergeEmployeeCells = (sheet, rows) => {
  let start = 0;
  for (let i = 1; i <= rows.length; i += 1) {
    const isBoundary =
      i === rows.length ||
      (rows[i]?.userId && rows[start]?.userId
        ? rows[i].userId !== rows[start].userId
        : rows[i]?.empName !== rows[start]?.empName);
    if (isBoundary) {
      if (i - 1 > start) {
        sheet.mergeCells(start + 2, 1, i + 1, 1);
        sheet.getCell(start + 2, 1).alignment = { vertical: "middle", horizontal: "left" };
      }
      start = i;
    }
  }
};

/**
 * Generates and downloads the styled Timesheet Report workbook with two sheets:
 * `User Summary` and `Project Breakdown`.
 *
 * @param {Array<Object>} userRows - User summary rows (each with a `projects` array)
 * @param {Object} totals - Grand totals (rounded numbers) for the TOTAL rows
 * @returns {Promise<void>} Resolves once the download has been triggered
 */
export const exportTimesheetReportExcel = async (userRows = [], totals = {}) => {
  const workbook = new ExcelJS.Workbook();

  const summaryRows = userRows.map((row) => ({
    ...row,
    empName: row.isGlobal
      ? `${row.empName || "-"} (Global User)`
      : row.empName || "-",
  }));
  buildStyledSheet(
    workbook,
    "User Summary",
    summaryRows,
    { ...totals, empName: "TOTAL" },
    false
  );

  const projectRows = [];
  userRows.forEach((row) => {
    const empDisplayName = row.isGlobal
      ? `${row.empName || "-"} (Global User)`
      : row.empName || "-";
    (Array.isArray(row.projects) ? row.projects : []).forEach((proj) => {
      projectRows.push({
        ...proj,
        userId: row.userId,
        empName: empDisplayName,
        projectName: proj.projectName || "-",
      });
    });
  });
  const projectSheet = buildStyledSheet(
    workbook,
    "Project Breakdown",
    projectRows,
    { ...totals, empName: "TOTAL", projectName: "" },
    true
  );
  mergeEmployeeCells(projectSheet, projectRows);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "Timesheet_Report.xlsx";
  document.body.appendChild(anchor);
  anchor.click();

  setTimeout(() => {
    if (document.body.contains(anchor)) {
      document.body.removeChild(anchor);
    }
    window.URL.revokeObjectURL(url);
  }, 1500);
};
